import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import type { AppRouterClient } from "@pos-vidyon/api/routers/index";
import { env } from "@pos-vidyon/env/web";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

function captureQueryError(error: Error, context?: Record<string, unknown>) {
  try {
    Sentry.captureException(error, { extra: context });
  } catch {
    // Sentry may not be initialized in development; ignore failures.
  }
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Don't retry on auth errors
          const status = (error as any)?.status;
          if (status === 401 || status === 403) return false;
          // Retry up to 2 times for other errors (network, 500, etc.)
          return failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
        // Keep serving stale data while revalidating in background
        staleTime: 30_000,
        // Always serve cached data while offline; revalidate once we're back online
        networkMode: "offlineFirst",
        refetchOnReconnect: "always",
      },
      mutations: {
        retry: (failureCount, error) => {
          const status = (error as any)?.status;
          if (status === 401 || status === 403 || status === 400) return false;
          return failureCount < 1;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Avoid noise from routine auth redirects and navigation aborts.
        const message = error.message?.toLowerCase() || "";
        if (
          !message.includes("unauthorized") &&
          !message.includes("401") &&
          error.name !== "AbortError"
        ) {
          captureQueryError(error as Error, {
            queryKey: query.queryKey,
          });
        }
        toast.error(`Error: ${error.message}`, {
          action: {
            label: "retry",
            onClick: () => {
              query.invalidate();
            },
          },
        });
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation) => {
        // Block mutations while the browser reports no network connection. This
        // prevents users from placing orders, taking payments, etc. while offline
        // and thinking the system crashed when the request inevitably fails.
        const isOnline = typeof window !== "undefined" ? navigator.onLine : true;
        if (!isOnline) {
          const err = new Error(
            "Bu işlem için internet bağlantısı gereklidir. Lütfen bağlantınızı kontrol edip tekrar deneyin.",
          );
          (err as any).status = 0;
          (err as any).__offline = true;
          toast.error(err.message);
          // Tag the mutation so onError can skip Sentry reporting / duplicate toast.
          (mutation as any).__blockedOffline = true;
          throw err;
        }
      },
      onError: (error, variables, _context, mutation) => {
        if ((mutation as any).__blockedOffline || (error as any).__offline) {
          // Already reported via toast in onMutate; no need to alert Sentry.
          return;
        }
        captureQueryError(error as Error, {
          mutationKey: mutation.options.mutationKey,
          variables,
        });
      },
    }),
  });
}

export const queryClient = createQueryClient();

function getServerUrl(url: string) {
  const normalized = url.endsWith("/") ? url.slice(0, -1) : url;

  if (!normalized.startsWith("/")) {
    return normalized;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalized}`;
  }

  const processEnv = (
    globalThis as {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process?.env;
  const vercelUrl =
    processEnv?.VERCEL_ENV === "production"
      ? (processEnv?.VERCEL_PROJECT_PRODUCTION_URL ?? processEnv?.VERCEL_URL)
      : (processEnv?.VERCEL_URL ?? processEnv?.VERCEL_PROJECT_PRODUCTION_URL);
  if (vercelUrl) {
    const origin = vercelUrl.startsWith("http") ? vercelUrl : `https://${vercelUrl}`;
    return `${origin}${normalized}`;
  }

  return `http://localhost:3000${normalized}`;
}
export const link = new RPCLink({
  url: `${getServerUrl(env.VITE_SERVER_URL)}/rpc`,
  fetch(url, options) {
    const token = typeof window !== "undefined" ? localStorage.getItem("bearer_token") : null;
    const fetchOptions = options as RequestInit;
    const headers = {
      ...fetchOptions?.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    return fetch(url, {
      ...fetchOptions,
      headers,
      credentials: "include",
    });
  },
});

export const client: AppRouterClient = createORPCClient(link);

export const orpc = createTanstackQueryUtils(client);
